import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { sendOtp, login } from "../../../redux/actions/authActions";
import { fetchSiteConfig } from "../../../redux/actions/dataActions";
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { LoadingButton } from '@mui/lab';
import { getTenant } from "../../../config/tenants";
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import MarkChatReadRoundedIcon from '@mui/icons-material/MarkChatReadRounded';
import WavingHandRoundedIcon from '@mui/icons-material/WavingHandRounded';
import { useI18n } from "../../../i18n";

import "./Login.css";

// Code-validity countdown, mirroring the dashboard's inline-OTP timer (5 minutes):
// shows "נותרו MM:SS", then "הקוד פג". Resending restarts it.
const OTP_VALID_SECONDS = 300;

/**
 * Hook: listens for an incoming SMS via the Web OTP API
 * and calls onCode(code) when it arrives.
 *
 * @param {(code: string) => void} onCode
 * @param {boolean} trigger – start listening when true
 */

const Login = ({ emergencyText }) => {
  const { otp, otpChannel, otpTo, errorMsg } = useSelector((state) => state.auth);
  const { loading }      = useSelector((state) => state.async);
  const dispatch         = useDispatch();
  const { t, dir }       = useI18n();

  const [phone, setPhone]     = useState("");
  const [otpValue, setOtp]    = useState("");
  const [terms]               = useState(true);

  /* ★★ C2a — ערוץ קבלת הקוד.
     `emailChannel` הוא **המתג של הלקוח** (מגיע מ-site-config); כשהוא כבוי
     הקישור אינו קיים כלל והמסך זהה לחלוטין למה שהיה עד היום — ולכן אף לקוח
     שלא הפעיל את הפיצ'ר אינו רואה שינוי.
     `useEmail` הוא הבחירה של האזרח **במסך הזה** בלבד; ההעדפה הנשמרת יושבת
     בשרת, וה-`channel` שחוזר משליחת-הקוד הוא מקור-האמת לטקסט שמוצג. */
  const [emailChannel, setEmailChannel] = useState(false);
  const [useEmail, setUseEmail] = useState(false);
  const [email, setEmail]       = useState("");
  const [secondsLeft, setSecondsLeft] = useState(0);
  // shown when a 401 ended the session (e.g. the account signed in on another device — see the axios
  // interceptor in setAuthToken.js), so the user understands why they're back at login.
  const [sessionEnded, setSessionEnded] = useState(false);
  const otpInputRef = useRef(null);

  useEffect(() => {
    if (sessionStorage.getItem("sessionEnded")) {
      setSessionEnded(true);
      sessionStorage.removeItem("sessionEnded");
    }
  }, []);

  /* ★ C2a — האם הלקוח הדליק את ערוץ-המייל. נטען פעם אחת; כישלון ⇒ נשאר כבוי,
     כלומר ההתנהגות הקיימת. */
  useEffect(() => {
    let alive = true;
    dispatch(fetchSiteConfig()).then((cfg) => {
      if (alive && cfg && cfg.email_channel) { setEmailChannel(true); }
    }).catch(() => {});
    return () => { alive = false; };
  }, [dispatch]);

  // Determine when OTP form is visible
  const otpFormVisible = Boolean(otp && otp !== "fresh");

  /* ★ הערוץ שבו הקוד **באמת** נשלח, כפי שדיווח השרת. לא נגזר מ-`useEmail`:
     השרת עשוי לנתב לפי העדפה שמורה, או ליפול חזרה ל-SMS אם המייל נכשל —
     והמסך חייב לומר את האמת ולא את הכוונה. */
  const sentChannel = otpChannel || "sms";
  const sentTo      = otpTo || "";
  const sentByEmail = sentChannel === "email";

  // Robust OTP capture for Android/Samsung: some keyboards fill the code via the Autofill
  // framework or a clipboard "paste" chip that does NOT trigger React's onChange — so the field
  // looked empty and the code never registered. Attach native input/change listeners directly to
  // the DOM node, plus a light poll while the code step is open, and mirror whatever lands in the
  // field into state. Belt-and-suspenders on top of the controlled onChange/onPaste below.
  useEffect(() => {
    if (!otpFormVisible) return undefined;
    const el = otpInputRef.current;
    if (!el) return undefined;
    const sync = () => {
      const digits = (el.value || "").replace(/\D/g, "").slice(0, 6);
      setOtp((prev) => (prev === digits ? prev : digits));
    };
    el.addEventListener("input", sync);
    el.addEventListener("change", sync);
    const poll = setInterval(sync, 400); // fallback for autofill that sets .value with no event
    return () => {
      el.removeEventListener("input", sync);
      el.removeEventListener("change", sync);
      clearInterval(poll);
    };
  }, [otpFormVisible]);

  // Start / restart the code-validity countdown whenever the OTP step appears.
  useEffect(() => {
    if (otpFormVisible) setSecondsLeft(OTP_VALID_SECONDS);
  }, [otpFormVisible]);

  // Tick the countdown down to 0 (setTimeout self-reschedules via the dependency).
  useEffect(() => {
    if (secondsLeft <= 0) return undefined;
    const id = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [secondsLeft]);

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  // URL spec-token auto-login. Two carriers:
  //  1) ?spec_token=…  (legacy query-string deep-links).
  //  2) #t=…           (the day-before REMINDER deep-link, /go → /appointment/:id#t=…). The token
  //     rides in the URL *fragment* — which browsers never send to the server — so the session
  //     credential stays out of access logs / Referer headers.
  useEffect(() => {
    let specToken = new URLSearchParams(window.location.search).get("spec_token");
    if (!specToken && window.location.hash) {
      specToken = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("t");
    }
    if (specToken) {
      dispatch(login({ spec_token: specToken }));
    }
  }, [dispatch]);

  // Web OTP API — Android Chrome, secure context (HTTPS / localhost) only. When the OTP form is
  // shown we start listening for the incoming SMS and fill the 6-digit code automatically (the SMS
  // ends with the "@<host> #<code>" binding line that the server adds). iOS has no such API — there
  // the input's autocomplete="one-time-code" provides the keyboard suggestion. No-ops elsewhere.
  useEffect(() => {
    if (!otpFormVisible || !("OTPCredential" in window)) return;
    const ac = new AbortController();
    navigator.credentials
      .get({ otp: { transport: ["sms"] }, signal: ac.signal })
      .then((cred) => {
        const code = cred && (cred.code || (typeof cred === "string" ? cred : ""));
        if (code) setOtp(code);
      })
      .catch(() => { /* aborted or unsupported — ignore */ });
    return () => ac.abort();
  }, [otpFormVisible]);

  // The backend matches appointments by exact mobile string, and browser
  // autofill can supply an international format (+972...). Normalise to the
  // local 0-prefixed format so the lookup is consistent however it was entered.
  const normalizePhone = (raw) => {
    let p = (raw || "").replace(/[\s\-()]/g, "");
    if (p.startsWith("+972")) p = "0" + p.slice(4);
    else if (p.startsWith("972")) p = "0" + p.slice(3);
    return p;
  };

  /* ★ C2a — מטען-הבקשה לשליחת-קוד. `channel` נשלח **רק** כשהאזרח בחר במפורש;
     בלעדיו השרת מכריע לפי ההעדפה השמורה שלו, וזה בדיוק הרצוי בכניסה חוזרת. */
  const otpPayload = () => {
    const p = { phone: normalizePhone(phone), recaptcha: "token" };
    if (emailChannel && useEmail) { p.channel = "email"; p.email = email.trim(); }
    return p;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const normPhone = normalizePhone(phone);
    if (!otpFormVisible) {
      dispatch(sendOtp(otpPayload()));
    } else {
      dispatch(login({ otp: otpValue, phone: normPhone }));
    }
  };

  // Auto-submit once the 6-digit code is complete — whether typed, pasted, or auto-filled from the
  // SMS ("כן, זה בסדר"). Saves the extra "אישור" tap. Re-arms when the code changes, so correcting a
  // wrong code re-submits automatically; guarded by `loading` and the last-submitted value.
  const lastAutoSubmit = useRef("");
  useEffect(() => {
    if (!otpFormVisible) { lastAutoSubmit.current = ""; return; }
    if (otpValue.length === 6 && !loading && otpValue !== lastAutoSubmit.current) {
      lastAutoSubmit.current = otpValue;
      dispatch(login({ otp: otpValue, phone: normalizePhone(phone) }));
    }
  }, [otpValue, otpFormVisible, loading, phone, dispatch]);

  // Re-send the code (no full page reload) and restart the validity countdown —
  // available any time (like the dashboard), guarded only against a mid-flight resend.
  const resendOtp = () => {
    if (loading) return;
    dispatch(sendOtp(otpPayload()));
    setSecondsLeft(OTP_VALID_SECONDS);
    setOtp("");
  };

  // Per-tenant vertical offset for the login form (single config source).
  const login_form_margin_top = getTenant().loginFormMarginTop;

 if (emergencyText) {
    return (
      <div
        className="login_page"
        style={{ marginTop: login_form_margin_top }}
        
      >
        <div className="r_side">
        
          <h2 className="login_title">{t("login.systemTitle")}</h2>

          {/* ⚠️ טקסט-החירום מגיע מהשרת כ-HTML של הלקוח והוא **אינו מתורגם** —
              הוא הודעה נקודתית שהרשות כתבה בעצמה. ראה F1.6 (תוכן-לקוח). */}
          <div
            className="emergency-text"
            dangerouslySetInnerHTML={{ __html: emergencyText }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="login_page" style={{ marginTop: login_form_margin_top }}>
      <div className="r_side">
        <Box
          /* ★★ C2a — במסלול-המייל הכרטיס גבוה בכ-90px (שדה נוסף + קישור +
             כותרת-משנה שנשברת לשתי שורות). במובייל אין 90px פנויים: הפוטר
             (‏`.ans-mobile-footer`) והכפתור הם `position:fixed`, ולכן הכרטיס
             **נדחף מתחתיהם** במקום לדחוף אותם — והקישור נחת על הצהרת-הנגישות.
             הקלאס הזה מאפשר לצמצם את הקישוט במובייל בלבד. ראה Login.css. */
          className={"box_login" + (useEmail ? " box_login--email" : "")}
          component="form"
          onSubmit={handleSubmit}
          noValidate
          autoComplete="on"
        >
          {/* Friendly brand accent — swaps to a "message received" mark on the OTP step. */}
          <div className="login-badge" aria-hidden="true">
            {otpFormVisible ? <MarkChatReadRoundedIcon /> : <EventAvailableRoundedIcon />}
          </div>

          {sessionEnded && !otpFormVisible && (
            <div className="login-session-note" role="status">
              {t("login.sessionEnded")}
            </div>
          )}

          {otpFormVisible ? (
            <>
              <h2 className="login_title">{t("login.otpTitle")}</h2>
              {/* ★ C2a — הכיתוב מדווח לאן הקוד נשלח **בפועל** (לפי תשובת השרת). */}
              <p className="login_subtitle">
                {sentByEmail
                  ? t("login.otpSubtitleEmail", { to: sentTo })
                  : t("login.otpSubtitle")}
              </p>
            </>
          ) : (
            <>
              <h2 className="login-welcome">{t("login.welcome")} <WavingHandRoundedIcon className="welcome-wave" /></h2>
              <p className="login_subtitle">
                {t("login.subtitle")}<br />
                {useEmail ? t("login.subtitle2Email") : t("login.subtitle2")}
              </p>
              <p className="login-note">{useEmail ? t("login.noteEmail") : t("login.note")}</p>
            </>
          )}

          <div className="content_login_form">
            {(!otp || otp === 'fresh') && (
              <TextField
                className="input_tel"
                style={{ width: '100%' }}
                required
                name="phone"
                autoComplete="tel"
                value={phone}
                label={t("login.phoneLabel")}
                onChange={(e) => setPhone(e.target.value)}
                placeholder={t("login.phonePlaceholder")}
                type="tel"
              />
            )}

            {/* ★★ C2a — מסלול-המייל. מוצג **רק** אם הלקוח הדליק את המתג
                (`emailChannel`), ולכן אצל לקוח שלא הפעיל אותו המסך זהה
                לחלוטין למה שהיה. הנייד נשאר שדה-חובה גם כאן — הוא הזהות. */}
            {emailChannel && (!otp || otp === 'fresh') && (
              <>
                {useEmail && (
                  <TextField
                    className="input_email"
                    style={{ width: '100%', marginTop: 12, direction: 'ltr' }}
                    required
                    name="email"
                    autoComplete="email"
                    value={email}
                    label={t("login.emailLabel")}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("login.emailPlaceholder")}
                    type="email"
                  />
                )}
                <div
                  className="login-channel-link"
                  role="button"
                  tabIndex={0}
                  onClick={() => setUseEmail((v) => !v)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setUseEmail((v) => !v); }
                  }}
                >
                  {useEmail ? t("login.smsLink") : t("login.emailLink")}
                </div>
              </>
            )}

            {otpFormVisible && (
              /* reveal-animated so the code field slides + fades in (Steps.css keyframe) */
              <div className="otp_form otp_form--reveal">
                <TextField
                  className="input_otp"
                  /* ★ F1 — היה `direction:'rtl'` קשיח. ‏`style` inline אינו עובר
                     דרך stylis, ולכן הוא **לא** התהפך מעצמו והיה כופה RTL על
                     שדה-הקוד גם ברוסית/אנגלית. הכיווניות נגזרת מהשפה. */
                  style={{ width: '100%', direction: dir }}
                  required
                  autoFocus
                  name="otp"
                  id="otp"
                  autoComplete="one-time-code"
                  inputRef={otpInputRef}
                  value={otpValue}
                  /* Keep only the digits, capped at 6. This makes paste/autofill robust: whether the
                     Samsung/Android keyboard fills just the code or drops the whole SMS text in, we
                     extract the numeric code instead of rejecting the input. */
                  onChange={(e) => setOtp((e.target.value || '').replace(/\D/g, '').slice(0, 6))}
                  /* Explicit paste handler — some Android keyboards' clipboard/autofill chip does not
                     trigger React's onChange; grab the pasted text ourselves and sanitize it. */
                  onPaste={(e) => {
                    const pasted = (e.clipboardData && e.clipboardData.getData('text')) || '';
                    const digits = pasted.replace(/\D/g, '').slice(0, 6);
                    if (digits) { e.preventDefault(); setOtp(digits); }
                  }}
                  label={t("login.otpLabel")}
                  /* ★ גם ה-placeholder אמר "שנשלח ב-SMS" — כיתוב שסותר את
                     מה שקרה בפועל כשהקוד נשלח למייל. */
                  placeholder={sentByEmail
                    ? t("login.otpPlaceholderEmail")
                    : t("login.otpPlaceholder")}
                  type="text"
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                    maxLength: 6,
                    autoComplete: 'one-time-code'
                  }}
                />
                {errorMsg && <div className="errorMsg">{errorMsg}</div>}

                {/* Wording matches the dashboard OTP area exactly: "נותרו MM:SS" /
                    "הקוד פג", and the resend link reads "שלח קוד מחדש". */}
                <div className={"otp-timer" + (secondsLeft <= 0 ? " otp-timer--expired" : "")} aria-live="polite">
                  {secondsLeft > 0
                    ? <>{t("login.remaining")} <span className="otp-countdown">{formatTime(secondsLeft)}</span></>
                    : t("login.codeExpired")}
                </div>
                <div className="restert" role="button" tabIndex={0}
                  onClick={resendOtp}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); resendOtp(); } }}>
                  {t("login.resend")}
                </div>
              </div>
            )}
          </div>

          <LoadingButton
            /* ★ C2a — במסלול-המייל גם המייל חובה: בלעדיו השרת היה מחזיר שגיאה
               אחרי לחיצה, ועדיף למנוע את הלחיצה מלכתחילה. */
            disabled={loading || (otpFormVisible
              ? otpValue.trim().length < 4
              : (!phone || (emailChannel && useEmail && !email.trim())))}
            loading={loading}
            onClick={handleSubmit}
            variant="contained"
            className="login-btn"
            fullWidth
            sx={{ borderRadius: 0, fontSize: 18 }}
          >
            {!otpFormVisible ? t("login.sendCode") : t("login.signIn")}
          </LoadingButton>
        </Box>
      </div>
    </div>
  );
};

export default Login;
